// ============================================================
// fieldFocusRequest.ts — "take me to that field".
// The case page's required-fields card asks for a field (its validation key,
// e.g. 'accidentDate') on a section, then navigates there. Whichever card
// engine owns that field (SectionCards / ClientInfoSections) takes the
// request when it mounts — or straight away if it's already on screen —
// opens the card holding the field for editing, and focuses the field.
// A request is for ONE section and goes stale after a few seconds, so a
// later screen never picks up an old one.
// ============================================================
import { useEffect, useRef } from 'react'
import { useCasevoStore } from '../store/useCasevoStore'
import type { ViewingPaneHeading } from '../types/case.types'

export interface FieldFocusRequest {
  section: ViewingPaneHeading
  // The validation key (ValidationError.field).
  field: string
  // The field's data-field key, when it has one (focus lands inside it).
  dataField?: string
  at: number
}

const STALE_MS = 5000
let pending: FieldFocusRequest | null = null
const listeners = new Set<() => void>()

export const requestFieldFocus = (r: Omit<FieldFocusRequest, 'at'>): void => {
  pending = { ...r, at: Date.now() }
  listeners.forEach((l) => l())
}
export const peekFieldFocusRequest = (): FieldFocusRequest | null => {
  if (pending && Date.now() - pending.at > STALE_MS) pending = null
  return pending
}
export const clearFieldFocusRequest = (): void => { pending = null }

const FOCUSABLE = 'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"], button:not([disabled])'
const isEmptyTextLike = (el: Element): boolean => {
  if (el instanceof HTMLInputElement) return !['checkbox', 'radio', 'button', 'submit'].includes(el.type) && el.value.trim() === ''
  if (el instanceof HTMLTextAreaElement) return el.value.trim() === ''
  if (el instanceof HTMLElement && el.isContentEditable) return (el.textContent ?? '').trim() === ''
  return false
}
// Focus inside `root`: the first EMPTY text-like input (what's missing), else
// the first focusable control. Returns false when there's nothing to focus.
export const focusWithin = (root: Element): boolean => {
  const all = Array.from(root.querySelectorAll(FOCUSABLE)) as HTMLElement[]
  const target = all.find(isEmptyTextLike) ?? all[0]
  if (!target) return false
  target.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
  target.focus({ preventScroll: true })
  return true
}

// For a card engine. `cardFor` names the card that owns a validation key (or
// null — not ours); `openCard` puts that card into editing; `isCardOpen`
// says when it's editing (focus waits for that — a read-only card's pencil
// would otherwise take focus and then vanish); `cardSelector` finds the
// card's element (the fallback focus root when the field has no data-field).
export const useFieldFocusRequest = (
  cardFor: (field: string) => string | null,
  openCard: (cardKey: string) => void,
  isCardOpen: (cardKey: string) => boolean,
  cardSelector: (cardKey: string) => string,
): void => {
  const latest = useRef({ cardFor, openCard, isCardOpen, cardSelector })
  latest.current = { cardFor, openCard, isCardOpen, cardSelector }
  useEffect(() => {
    let frame = 0
    let cancelled = false
    const handle = (): void => {
      const req = peekFieldFocusRequest()
      if (!req || useCasevoStore.getState().activeCaseSection !== req.section) return
      const { cardFor: owner, openCard: open, cardSelector: sel } = latest.current
      const key = owner(req.field)
      if (!key) return
      clearFieldFocusRequest()
      open(key)
      // Wait for the card's inputs to render (a few frames at most).
      let tries = 0
      const attempt = (): void => {
        if (cancelled) return
        if (!latest.current.isCardOpen(key)) { if (++tries < 20) frame = window.requestAnimationFrame(attempt); return }
        const byField = req.dataField ? document.querySelector(`[data-field="${req.dataField}"]`) : null
        const card = document.querySelector(sel(key))
        if ((byField && focusWithin(byField)) || (card && focusWithin(card))) return
        if (++tries < 20) frame = window.requestAnimationFrame(attempt)
      }
      frame = window.requestAnimationFrame(attempt)
    }
    handle()
    listeners.add(handle)
    return () => { cancelled = true; listeners.delete(handle); window.cancelAnimationFrame(frame) }
  }, [])
}
